import { type FC, type KeyboardEvent, useCallback, useState } from "react";
import type { MetaAdsGeoSearchResult, MetaAdsZipCodeTarget } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { useGeoSearch, useValidateZipCodes } from "../useGeoSearch";
import { useGeoTargeting } from "../useGeoTargeting";

export const ZipCodePanel: FC = () => {
	const { geo, addZipCode, addZipCodes, removeZipCode } = useGeoTargeting();
	const [input, setInput] = useState("");
	const [debouncedQuery] = useDebounceValue(input, 400);
	const { data: searchResults, isFetching } = useGeoSearch(
		debouncedQuery,
		"zip",
	);
	const validateMutation = useValidateZipCodes();

	const zipCodes = geo.zipCodes ?? [];

	const handleBulkAdd = useCallback(() => {
		const raw = input
			.split(/[,\n\s]+/)
			.map((s) => s.trim())
			.filter(Boolean);
		if (raw.length === 0) return;

		validateMutation.mutate(
			{ zipCodes: raw },
			{
				onSuccess: (results) => {
					const targets: MetaAdsZipCodeTarget[] = results.map(toZipTarget);
					addZipCodes(targets);
					setInput("");
				},
			},
		);
	}, [input, validateMutation, addZipCodes]);

	const handleSearchSelect = useCallback(
		(result: MetaAdsGeoSearchResult) => {
			addZipCode(toZipTarget(result));
			setInput("");
		},
		[addZipCode],
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				e.preventDefault();
				if (searchResults && searchResults.length > 0) {
					handleSearchSelect(searchResults[0]);
				} else {
					handleBulkAdd();
				}
			}
		},
		[searchResults, handleSearchSelect, handleBulkAdd],
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div>
				<input
					type="text"
					className="form-control form-control-sm"
					placeholder="Search zip codes or paste multiple (comma-separated)..."
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
			</div>

			{/* Search results dropdown */}
			{debouncedQuery.length >= 2 &&
				searchResults &&
				searchResults.length > 0 && (
					<div
						className="list-group"
						style={{ maxHeight: 160, overflowY: "auto" }}
					>
						{searchResults.map((r) => (
							<button
								key={r.key}
								type="button"
								className="list-group-item list-group-item-action py-1 px-2"
								style={{ fontSize: 13 }}
								onClick={() => handleSearchSelect(r)}
							>
								{r.name}
							</button>
						))}
					</div>
				)}

			{isFetching && (
				<div className="text-muted" style={{ fontSize: 12 }}>
					Searching...
				</div>
			)}

			{/* Bulk validate button */}
			{input.includes(",") || input.includes("\n") ? (
				<button
					type="button"
					className="btn btn-sm btn-outline-primary"
					onClick={handleBulkAdd}
					disabled={validateMutation.isPending}
				>
					{validateMutation.isPending ? "Validating..." : "Validate & Add All"}
				</button>
			) : null}

			{/* Selected zips as chips */}
			{zipCodes.length > 0 && (
				<div>
					<div
						style={{
							fontSize: 12,
							color: "#6c757d",
							marginBottom: 4,
						}}
					>
						{zipCodes.length} zip code
						{zipCodes.length !== 1 ? "s" : ""} selected
					</div>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 4,
						}}
					>
						{zipCodes.map((zip) => (
							<button
								type="button"
								key={zip.key}
								className="badge bg-primary border-0"
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 4,
									cursor: "pointer",
									fontSize: 12,
								}}
								onClick={() => removeZipCode(zip.key)}
							>
								{zip.name}
								<span style={{ fontWeight: "bold" }}>&times;</span>
							</button>
						))}
					</div>
					<button
						type="button"
						className="btn btn-sm btn-link text-danger p-0 mt-1"
						onClick={() => {
							for (const zip of zipCodes) {
								removeZipCode(zip.key);
							}
						}}
					>
						Remove All
					</button>
				</div>
			)}
		</div>
	);
};

function toZipTarget(r: MetaAdsGeoSearchResult): MetaAdsZipCodeTarget {
	return {
		key: r.key,
		name: r.name,
		primaryCity: r.primary_city ?? "",
		region: r.region ?? "",
		country: r.country_code,
	};
}
